// Phase VPD targets: [low, high] in kPa. Phases without a target return null.
export const VPD_PHASE_TARGETS = {
  Pinning:    [0.4, 0.8],
  Fruiting:   [0.2, 0.5],
}

// Computes a 0–100 environmental stability score from a VPD series vs. the
// current phase's target band. Returns null if the phase has no target or the
// series is empty.
//
// Method: mean absolute deviation from the nearest target-band edge (0 if
// inside the band). Mapped to 0–100 where 0 deviation → 100, and deviation
// ≥ 0.5 kPa → 0.
export function computeStabilityScore(vpdSeries, phaseName) {
  if (!vpdSeries?.length) return null
  const target = VPD_PHASE_TARGETS[phaseName]
  if (!target) return null

  const [lo, hi] = target
  let totalDeviation = 0
  for (const { v } of vpdSeries) {
    if (v < lo) totalDeviation += lo - v
    else if (v > hi) totalDeviation += v - hi
    // inside band: 0 deviation
  }
  const meanDev = totalDeviation / vpdSeries.length
  // 0.5 kPa mean deviation maps to score 0; linear above gives negative, clamp to 0
  return Math.max(0, Math.round(100 * (1 - meanDev / 0.5)))
}

export function computeVPD(tempF, rh) {
  const tC = (tempF - 32) * 5 / 9
  const es = 0.6108 * Math.exp(17.27 * tC / (tC + 237.3))
  return es * (1 - rh / 100)
}

// Zips two {t, v} point arrays by nearest timestamp and returns a {t, v} VPD series.
export function computeVPDSeries(tempPoints, humidityPoints) {
  if (!tempPoints?.length || !humidityPoints?.length) return []

  const humMap = new Map(humidityPoints.map(p => [p.t, p.v]))

  // Build a sorted list of humidity timestamps for nearest-neighbor lookup
  const humTimes = humidityPoints.map(p => p.t).sort((a, b) => a - b)

  return tempPoints.map(({ t, v: tempF }) => {
    // Binary search for nearest humidity timestamp
    let lo = 0, hi = humTimes.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (humTimes[mid] < t) lo = mid + 1
      else hi = mid
    }
    // Check both neighbors and pick closer
    const candidates = [humTimes[lo]]
    if (lo > 0) candidates.push(humTimes[lo - 1])
    const nearest = candidates.reduce((a, b) => Math.abs(a - t) <= Math.abs(b - t) ? a : b)

    const rh = humMap.get(nearest)
    if (rh == null) return null
    return { t, v: computeVPD(tempF, rh) }
  }).filter(Boolean)
}
